import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Modal, Select, Space, Switch, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request, { type CustomAxiosRequestConfig } from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { confirmDialog } from "@/utils/confirmDialog";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { ADMIN_PUBLISH_PLAT_SAND, PUBLISH_STATUS } from "@zhihaoscm/meta";
import { useUserStore } from "@/views/stores";
import type { NotificationItem, NotificationPageResult, NotificationQuery } from "./types";
import { showRequestError } from "@/utils/requestErrorMessage";
import "./NotificationPage.scss";

const defaultQuery: NotificationQuery = { page: 1, size: 10, sortKey: "edit_time", sortOrder: "desc" };

const classifyLabels: Record<number, string> = { 1: "系统通知", 2: "运营通知" };
const stateLabels: Record<number, string> = { 1: "草稿", 2: "待发布", 3: "已发布" };
const publishPlatLabels: Record<number, string> = { 1: "船务PC端", 2: "船务移动端", 3: "砂石PC端", 4: "砂石移动端" };
const publishPlatFilterOptions = ADMIN_PUBLISH_PLAT_SAND.options
    .filter(option => option.value)
    .map(option => ({ label: option.label, value: Number(option.value) }));

/** 按旧 Vue 通知列表的 DATETIME_PICKER_FORMAT 展示时间，隐藏秒和 ISO 分隔符。 */
function formatNotificationListTime(value?: string): string {
    return dateFormat(value, DATETIME_PICKER_FORMAT);
}

type NotificationFilterValues = {
    searchKeywords?: string;
    classify?: number;
    state?: number;
    publishPlats?: number[];
};

export interface NotificationPageProps {
    deleteNotification?: (id: number | string) => Promise<void>;
    loadNotifications?: (query: NotificationQuery) => Promise<NotificationPageResult>;
    publishNotification?: (id: number | string, publishTime?: string) => Promise<void>;
    revokeNotification?: (id: number | string) => Promise<void>;
    unpublishNotification?: (id: number | string) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 平台通知分页接口。 */
export async function loadNotifications(query: NotificationQuery): Promise<NotificationPageResult> {
    const response = await request.service.get<NotificationPageResult>("/dataService/admin/notification/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 平台通知发布接口，默认立即发布。 */
export async function publishNotification(id: number | string, publishTime = ""): Promise<void> {
    await request.service.put(`/dataService/admin/notification/publish/${id}`, { publishTime }, {
        // 页面会展示接口的具体失败原因，避免请求层和页面各弹一次错误提示。
        suppressGlobalError: true
    } as CustomAxiosRequestConfig);
}

/** 复用 Vue 平台通知取消发布接口。 */
export async function unpublishNotification(id: number | string): Promise<void> {
    await request.service.put(`/dataService/admin/notification/unpublished/${id}`, undefined, {
        // 页面会展示接口的具体失败原因，避免请求层和页面各弹一次错误提示。
        suppressGlobalError: true
    } as CustomAxiosRequestConfig);
}

/** 复用 Vue 平台通知撤回接口。 */
export async function revokeNotification(id: number | string): Promise<void> {
    await request.service.put(`/dataService/admin/notification/revoke/${id}`, undefined, {
        // 页面会展示接口的具体失败原因，避免请求层和页面各弹一次错误提示。
        suppressGlobalError: true
    } as CustomAxiosRequestConfig);
}

/** 复用 Vue 平台通知删除接口。 */
export async function deleteNotification(id: number | string): Promise<void> {
    await request.service.delete(`/dataService/admin/notification/${id}`, {
        // 页面会展示接口的具体失败原因，避免请求层和页面各弹一次错误提示。
        suppressGlobalError: true
    } as CustomAxiosRequestConfig);
}

/** 将 Vue 通知分页返回的枚举字段统一为 React 文案和状态动作使用的数字。 */
function normalizeNotificationRows(rows?: NotificationItem[]): NotificationItem[] {
    return (rows || []).map(row => ({
        ...row,
        notification: {
            ...row.notification,
            classify: row.notification.classify == null ? undefined : Number(row.notification.classify),
            state: row.notification.state == null ? undefined : Number(row.notification.state),
            publishPlatInfo: row.notification.publishPlatInfo?.map(value => Number(value))
        }
    }));
}

/** 平台通知列表，保留旧 Vue 的状态动作和通知管理权限。 */
export function NotificationPage({
    deleteNotification: remove = deleteNotification,
    loadNotifications: load = loadNotifications,
    publishNotification: publish = publishNotification,
    revokeNotification: revoke = revokeNotification,
    unpublishNotification: unpublish = unpublishNotification,
    permissions
}: NotificationPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 沿用 Vue 全局封禁态，保留通知写操作入口但阻止触发。
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canWrite = currentPermissions.includes("ROLE_SS_NOTICE_W");
    const [items, setItems] = useState<NotificationItem[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [query, setQuery] = useState<NotificationQuery>(defaultQuery);
    const [filterForm] = Form.useForm<NotificationFilterValues>();
    const [publishId, setPublishId] = useState<number | string>();
    const [publishVisible, setPublishVisible] = useState(false);
    const [timedPublish, setTimedPublish] = useState(false);
    const [publishDate, setPublishDate] = useState("");
    const [publishTime, setPublishTime] = useState("");

    const refresh = async (targetQuery: NotificationQuery = query) => {
        const result = await load(targetQuery);
        setItems(normalizeNotificationRows(result.list));
        setTotal(result.total || 0);
    };

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setItems(normalizeNotificationRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("平台通知加载失败");
                    setItems([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    // 将 Vue 通知列表筛选字段转换为后端分页查询参数。
    const submitFilters = (values: NotificationFilterValues) => {
        const filters = Object.fromEntries(
            Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== "")
        ) as Omit<NotificationQuery, "page" | "size" | "sortKey" | "sortOrder">;
        if (filters.classify !== undefined) filters.classify = Number(filters.classify);
        if (filters.state !== undefined) filters.state = Number(filters.state);
        if (filters.publishPlats) filters.publishPlats = filters.publishPlats.map(Number);
        setErrorMessage(null);
        setQuery({ ...defaultQuery, ...filters });
    };

    // 清空筛选条件并恢复 Vue 首屏排序分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(defaultQuery);
    };

    const runAction = async (
        action: (id: number | string, publishTime?: string) => Promise<void>,
        id: number | string | undefined,
        failureMessage: string,
        actionPublishTime?: string
    ) => {
        if (id == null) return;
        try {
            // 通知状态动作或刷新失败时保留当前列表，并展示对应的 Vue 兼容错误提示。
            if (actionPublishTime === undefined) {
                await action(id);
            } else {
                await action(id, actionPublishTime);
            }
        } catch (error) {
            // 请求层已关闭全局提示，保留后端具体原因并确保页面只弹一条消息。
            showRequestError(error, failureMessage);
            return;
        }

        try {
            await refresh();
            setErrorMessage(null);
        } catch (error) {
            // 刷新列表失败时请求层可能已经派发全局提示，此时不再追加页面兜底提示。
            showRequestError(error, failureMessage);
        }
    };

    /** 打开 Vue 列表发布确认弹窗，并在启用定时发布时默认填充五分钟后的时间。 */
    const openPublishDialog = (id: number | string | undefined) => {
        if (id == null) return;
        setPublishId(id);
        setTimedPublish(false);
        setPublishDate("");
        setPublishTime("");
        setPublishVisible(true);
    };

    /** 切换定时发布时复刻 Vue 的当前时间后五分钟默认值。 */
    const handleTimedPublishChange = (checked: boolean) => {
        setTimedPublish(checked);
        if (!checked) {
            setPublishDate("");
            setPublishTime("");
            return;
        }
        const target = new Date(Date.now() + 5 * 60 * 1000);
        const pad = (value: number) => String(value).padStart(2, "0");
        setPublishDate(`${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`);
        setPublishTime(`${pad(target.getHours())}:${pad(target.getMinutes())}`);
    };

    /** 将 Vue 自定义排序列转换为 zhihao Table 的分页接口字段和方向参数。 */
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (!prop || !["editTime", "publishTime"].includes(prop)) return;
            setQuery(currentQuery => {
                const nextQuery = { ...currentQuery, page: 1 };
                if (!order) {
                    nextQuery.sortKey = "";
                    nextQuery.sortOrder = "";
                } else {
                    nextQuery.sortKey = prop === "editTime" ? "edit_time" : "publish_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    /** 确认列表发布并按 Vue 格式提交即时或定时发布时间。 */
    const confirmPublish = async () => {
        if (publishId == null) return;
        if (timedPublish && (!publishDate || !publishTime)) {
            setErrorMessage("请选择发布时间");
            return;
        }
        // 旧 Vue notificationService.publish 使用 LOG_DATE_FORMAT_T，接口要求 T 分隔并带秒。
        const formattedPublishTime = timedPublish ? `${publishDate}T${publishTime}:00` : "";
        await runAction(publish, publishId, "平台通知发布失败", formattedPublishTime);
        setPublishVisible(false);
        setPublishId(undefined);
    };

    /** 复用旧 Vue MessageBox 确认状态动作，确认前不触发接口请求。 */
    const confirmStateAction = async (
        action: (id: number | string) => Promise<void>,
        id: number | string | undefined,
        messageText: string,
        failureMessage: string
    ) => {
        if (id == null || !(await confirmDialog(messageText))) return;
        await runAction(action, id, failureMessage);
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 通知 ProTable 的嵌套字段和状态操作。
    const columns: TableColumnProps<NotificationItem>[] = [
        {
            key: "id",
            label: "编号",
            width: 106,
            prop: "id",
            render: ({ row }) => (
                <Button
                    aria-label="查看通知详情"
                    type="link"
                    onClick={() => void navigate(`/userOperation/notificationInfo?id=${row.notification.id || ""}`)}
                >
                    {row.notification.id || "-"}
                </Button>
            )
        },
        {
            key: "title",
            label: "标题",
            minWidth: 350,
            prop: "notification.title",
            render: ({ row }) => row.notification.title || "-"
        },
        {
            key: "classify",
            label: "分类",
            minWidth: 176,
            prop: "notification.classify",
            render: ({ row }) => classifyLabels[row.notification.classify || 0] || "-"
        },
        {
            key: "state",
            label: "状态",
            minWidth: 78,
            prop: "notification.state",
            render: ({ row }) => stateLabels[row.notification.state || 0] || "-"
        },
        {
            key: "publishPlatInfo",
            label: "发布端",
            minWidth: 320,
            prop: "notification.publishPlatInfo",
            render: ({ row }) =>
                row.notification.publishPlatInfo?.map(value => publishPlatLabels[value] || value).join("、") || "-"
        },
        {
            key: "user",
            label: "创建人",
            minWidth: 176,
            prop: "user.name",
            render: ({ row }) => row.user?.name || "-"
        },
        {
            key: "editTime",
            label: "更新时间",
            width: 204,
            prop: "editTime",
            sortable: true,
            render: ({ row }) => formatNotificationListTime(row.notification.editTime)
        },
        {
            key: "publishTime",
            label: "发布时间",
            width: 204,
            prop: "publishTime",
            sortable: true,
            render: ({ row }) => formatNotificationListTime(row.notification.publishTime)
        },
        {
            key: "operation",
            label: "操作",
            width: 240,
            prop: "operation",
            fixed: "right",
            className: "admin-table-operation-column compact",
            labelClassName: "admin-table-operation-column compact",
            render: ({ row }) => {
                if (!canWrite) return null;
                const id = row.notification.id;
                const state = row.notification.state;
                return (
                    <Space>
                        {state === PUBLISH_STATUS.DRAFT.code ? (
                            <Button
                                aria-label="发布"
                                disabled={banButton}
                                size="small"
                                onClick={() => openPublishDialog(id)}
                            >
                                发布
                            </Button>
                        ) : null}
                        {state === PUBLISH_STATUS.PENDING.code ? (
                            <Button
                                aria-label="取消发布"
                                disabled={banButton}
                                size="small"
                                onClick={() => void runAction(unpublish, id, "平台通知取消发布失败")}
                            >
                                取消发布
                            </Button>
                        ) : null}
                        {state === PUBLISH_STATUS.PUBLISHED.code ? (
                            <Button
                                aria-label="撤回"
                                disabled={banButton}
                                size="small"
                                onClick={() => void confirmStateAction(revoke, id, "是否撤回？", "平台通知撤回失败")}
                            >
                                撤回
                            </Button>
                        ) : null}
                        {state === PUBLISH_STATUS.DRAFT.code ? (
                            <Button
                                aria-label="编辑通知"
                                disabled={banButton}
                                size="small"
                                onClick={() => void navigate(`/userOperation/NotificationEdit?id=${id || ""}`)}
                            >
                                编辑
                            </Button>
                        ) : null}
                        {state !== PUBLISH_STATUS.PENDING.code ? (
                            <Button
                                aria-label="删除通知"
                                danger
                                disabled={banButton}
                                size="small"
                                onClick={() => void confirmStateAction(remove, id, "是否删除？", "平台通知删除失败")}
                            >
                                删除
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    const defaultSort =
        query.sortKey === "edit_time"
            ? { prop: "editTime", order: query.sortOrder === "asc" ? ("ascending" as const) : ("descending" as const) }
            : query.sortKey === "publish_time"
              ? {
                    prop: "publishTime",
                    order: query.sortOrder === "asc" ? ("ascending" as const) : ("descending" as const)
                }
              : undefined;

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="notification-page">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<NotificationItem>
                    data={items}
                    columns={normalizeOperationColumns(columns, items)}
                    defaultSort={defaultSort}
                    pagination={false}
                    rowKey={row => String(row.notification.id || row.id)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                平台通知
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                initialValues={{ publishPlats: [] }}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canWrite ? (
                                    <Button
                                        aria-label="新增通知"
                                        className="admin-table-primary-action"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void navigate("/userOperation/notificationAdd")}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                                <Form.Item label="标题或编号" name="searchKeywords">
                                    <Input placeholder="请输入标题关键词或编号搜索" />
                                </Form.Item>
                                <Form.Item label="分类" name="classify">
                                    <Select
                                        allowClear
                                        aria-label="通知分类"
                                        options={Object.entries(classifyLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择分类"
                                    />
                                </Form.Item>
                                <Form.Item label="状态" name="state">
                                    <Select
                                        allowClear
                                        aria-label="通知状态"
                                        options={Object.entries(stateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="状态"
                                    />
                                </Form.Item>
                                <Form.Item label="发布端" name="publishPlats">
                                    <Select
                                        allowClear
                                        aria-label="通知发布端"
                                        maxTagCount={1}
                                        mode="multiple"
                                        options={publishPlatFilterOptions}
                                        placeholder="请选择发布端"
                                    />
                                </Form.Item>
                                <Button htmlType="submit" icon={<TableSearchIcon name="search" />} type="primary">
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置通知筛选"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={resetFilters}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        // 复刻 Vue ProTable：切换每页条数后回到第 1 页，翻页则保留当前条数。
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <Modal
                centered
                className="notification-publish-modal"
                open={publishVisible}
                title="是否发布"
                width={400}
                styles={{ body: { minHeight: 100, paddingTop: 17 } }}
                onCancel={() => setPublishVisible(false)}
                footer={
                    <Space>
                        <Button onClick={() => setPublishVisible(false)}>取消</Button>
                        <Button aria-label="确认发布" onClick={() => void confirmPublish()} type="primary">
                            发布
                        </Button>
                    </Space>
                }
            >
                <div className="notification-publish-dialog__row">
                    <span className="notification-publish-dialog__label">定时发布</span>
                    <Switch aria-label="定时发布" checked={timedPublish} onChange={handleTimedPublishChange} />
                </div>
                {timedPublish ? (
                    <Space className="notification-publish-dialog__date-time">
                        <Input
                            aria-label="发布时间日期"
                            type="date"
                            value={publishDate}
                            onChange={event => setPublishDate(event.target.value)}
                        />
                        <Input
                            aria-label="发布时间时间"
                            type="time"
                            value={publishTime}
                            onChange={event => setPublishTime(event.target.value)}
                        />
                    </Space>
                ) : null}
            </Modal>
        </section>
    );
}

export default NotificationPage;
